/**
 * ConfirmPH: Trust badges (public profile).
 *
 * Backend-computed signals only. The component renders exactly what the
 * `/api/public/rentals/{cid}/trust-badges` endpoint returns and never
 * invents a badge or merges in static copy. Each badge ships with its
 * own description so the tooltip text is also server-controlled.
 *
 * Wording is deliberately cautious: these are signals, NOT guarantees
 * of safety, legality, honesty, or scam-free operation.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, CheckCircle2, Star, FileText, Activity, CalendarCheck,
  AlertOctagon, MessageSquare, Smartphone, Info, ShieldAlert,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const ICONS = {
  enhanced_safety_active: ShieldAlert,
  profile_completed:     CheckCircle2,
  active_on_confirmph:   Activity,
  in_app_booking:        Smartphone,
  verified_reviews:      Star,
  registration_evidence_reviewed: FileText,
  fleet_registration_reviewed:    FileText,
  fleet_insurance_reviewed:       ShieldCheck,
  vehicle_registration_reviewed:  FileText,
  vehicle_insurance_reviewed:     ShieldCheck,
  no_critical_alerts:    ShieldCheck,
  responds_to_inquiries: MessageSquare,
  photos_available:      CheckCircle2,
  recently_inspected:    CalendarCheck,
  damage_records_maintained: AlertOctagon,
};

const TONES = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  amber:   "bg-amber-50 border-amber-200 text-amber-900",
  slate:   "bg-slate-50 border-slate-200 text-slate-800",
  indigo:  "bg-indigo-50 border-indigo-200 text-indigo-900",
};

export default function TrustBadges({ companyId, vehicleId = null, compact = false }) {
  const [state, setState] = useState({ loading: true, business: [], vehicle: [] });

  useEffect(() => {
    let cancelled = false;
    if (!companyId) return undefined;
    (async () => {
      try {
        const params = vehicleId ? { vehicle_id: vehicleId } : {};
        const r = await axios.get(`${API}/public/rentals/${companyId}/trust-badges`,
                                    { params });
        if (!cancelled) {
          setState({
            loading: false,
            business: r.data.business || [],
            vehicle: r.data.vehicle || [],
          });
        }
      } catch (_) {
        if (!cancelled) setState({ loading: false, business: [], vehicle: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, vehicleId]);

  const all = [...state.business, ...state.vehicle];
  if (state.loading || all.length === 0) return null;

  return (
    <section className={compact ? "" : "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2"}
             data-testid="trust-badges-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
            data-testid="trust-badges-heading">
          Profile signals
        </h3>
        <button
          type="button"
          className="text-[11px] text-slate-500 hover:text-slate-700 underline inline-flex items-center gap-1"
          onClick={() => {
            const el = document.getElementById("trust-badges-disclaimer");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
          data-testid="trust-badges-info-link"
        >
          <Info className="w-3 h-3" /> What do these mean?
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="trust-badges-list">
        {all.map((b) => (
          <BadgeChip key={b.key} badge={b} />
        ))}
      </div>
      <div
        id="trust-badges-disclaimer"
        className="mt-3 text-[11px] leading-relaxed text-slate-500"
        data-testid="trust-badges-disclaimer"
      >
        Profile signals are computed from ConfirmPH data (profile fields, uploaded
        documents, reviews tied to real bookings, inquiry responses, alerts).
        They show what the business has done on ConfirmPH: they are <em>not</em>
        a guarantee of safety, legality, or honesty. ConfirmPH does not guarantee
        document validity, legal status, or current compliance.
      </div>
    </section>
  );
}

function BadgeChip({ badge }) {
  const Icon = ICONS[badge.key] || CheckCircle2;
  const tone = TONES[badge.tone] || TONES.slate;
  return (
    <div
      className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium " + tone}
      title={badge.description || ""}
      data-testid={`trust-badge-${badge.key}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {badge.label}
    </div>
  );
}
